import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Section, StatusPill, EmptyState } from "@/components/app/Primitives";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ResultBadge } from "@/components/engage/ResultBadge";
import { ScenarioSwitcher } from "@/components/engage/ScenarioSwitcher";

const HOME_BY_ROLE = { ey_auditor: "/ey", procurement_desk: "/procurement", customer: "/customer", hul_approver: "/approver" };

export default function Campaigns() {
  const { user } = useAuth();
  const { id } = useParams();
  const [campaigns, setCampaigns] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (id) api.get(`/campaigns/${id}`).then((r) => setDetail(r.data)).catch(() => {});
    else api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, [id]);

  const downloadEYAuditPdf = async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/campaigns/${id}/ey-audit-trail.pdf`, { credentials: "include" });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ey-audit-${detail.campaign_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("EY audit-trail PDF downloaded");
    } catch (err) {
      toast.error(err?.message || "Download failed");
    }
  };

  const home = HOME_BY_ROLE[user?.role] || "/admin";

  if (id && detail) {
    const total = (detail.orders || []).reduce((a, o) => a + (o.order_value || 0), 0);
    return (
      <div className="space-y-6" data-testid="campaign-detail">
        <div className="text-sm text-slate-500">
          <Link className="hover:underline" to={`${home}/campaigns`}>← Campaigns</Link>
        </div>
        <Section
          title={detail.name}
          subtitle={`${detail.campaign_number} · ${detail.product || "—"} · Source: ${detail.source || "manual"} · Ref: ${detail.adfluence_ref || "n/a"}`}
          right={
            <Button
              variant="outline"
              onClick={downloadEYAuditPdf}
              className="border-[#0052FF] text-[#0052FF] hover:bg-[#0052FF]/5"
              data-testid="download-ey-audit-pdf-btn"
            >
              ⤓ EY Audit-Trail PDF
            </Button>
          }
        />
        <div className="grid grid-cols-3 gap-3">
          <Card><CardHeader className="pb-1"><CardTitle className="text-xs uppercase tracking-widest text-slate-500">Influencers</CardTitle></CardHeader><CardContent className="text-2xl font-heading font-black">{detail.orders?.length || 0}</CardContent></Card>
          <Card><CardHeader className="pb-1"><CardTitle className="text-xs uppercase tracking-widest text-slate-500">Total spend</CardTitle></CardHeader><CardContent className="text-2xl font-heading font-black">INR {Number(total).toLocaleString()}</CardContent></Card>
          <Card><CardHeader className="pb-1"><CardTitle className="text-xs uppercase tracking-widest text-slate-500">Status</CardTitle></CardHeader><CardContent><Badge variant="outline" className="capitalize">{detail.status}</Badge></CardContent></Card>
        </div>
        {detail.briefing_note && (
          <div className="rounded-lg border bg-slate-50 p-3 text-sm">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Briefing from AdFluence</div>
            <div className="text-slate-700">{detail.briefing_note}</div>
          </div>
        )}
        <Section title="Influencer engagements">
          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Order</th>
                  <th className="px-4 py-3 text-left">Influencer</th>
                  <th className="px-4 py-3 text-right">Value</th>
                  <th className="px-4 py-3 text-left">Advance</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Open</th>
                </tr>
              </thead>
              <tbody>
                {(detail.orders || []).map((o) => (
                  <tr key={o.id} className="border-t hover:bg-slate-50" data-testid={`campaign-order-${o.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{o.order_number}</td>
                    <td className="px-4 py-3">{o.influencer_name}<div className="text-xs text-slate-500">{o.influencer_email}</div></td>
                    <td className="px-4 py-3 text-right font-semibold">{o.currency} {Number(o.order_value).toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs">{o.advance_required ? `${o.advance_percentage}% · ${o.advance_trigger}` : "—"}</td>
                    <td className="px-4 py-3"><StatusPill status={o.status} /></td>
                    <td className="px-4 py-3 text-right"><Link to={`${home}/orders/${o.id}`}><Button size="sm" variant="outline">Open</Button></Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaigns-list">
      <Section
        title="Campaigns"
        subtitle={user?.role === "procurement_desk" ? "AdFluence-sourced campaigns · click New AdFluence Intake to add a fresh one." : "All campaigns visible to your account."}
        right={(
          <div className="flex items-center gap-3">
            <ScenarioSwitcher />
            {user?.role === "procurement_desk" && <Link to="/procurement/campaigns/new"><Button data-testid="new-adfluence-intake-btn" className="bg-[#0052FF] hover:bg-[#0043D9]">+ New AdFluence Intake</Button></Link>}
          </div>
        )}
      />
      {campaigns.length === 0 ? (
        <EmptyState title="No campaigns yet" hint="Campaigns arrive from AdFluence via ProPay Procurement intake." />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map((c) => (
            <Link key={c.id} to={`${home}/campaigns/${c.id}`} data-testid={`campaign-card-${c.id}`}>
              <Card className="hover:border-[#0052FF]/50 hover:shadow-md transition">
                <CardHeader>
                  <div className="text-[10px] font-mono text-slate-500">{c.campaign_number}</div>
                  <CardTitle className="text-base font-heading">{c.name}</CardTitle>
                  <div className="text-xs text-slate-500">{c.tenant_name} · {c.product || "—"}</div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">{c.orders_total} influencers</span>
                    <span className="font-mono font-semibold">INR {Number(c.spend || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-3">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">Pre-flight</span><ResultBadge value={c.preflight_status || "NOT_APPLICABLE"} />
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 ml-2 mr-1">Audit</span><ResultBadge value={c.audit_status || "NOT_APPLICABLE"} />
                  </div>
                  {c.demo_scenario && c.demo_scenario !== "happy" && <div className="mt-2 text-[10px] font-mono text-amber-700">scenario · {c.demo_scenario.replace(/_/g, " ")}</div>}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {Object.entries(c.orders_status || {}).map(([k, v]) => (
                      <Badge key={k} variant="outline" className="text-[10px] capitalize">{k.replace(/_/g, " ")}: {v}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
